<template>
  <div>
    <vue-draggable
      v-bind="dragOptions"
      v-model="groupList"
      tag="div"
      :data-type="(getElementById(groupId) && getElementById(groupId).type) || 'root'"
      :data-test="`graphics-layerGroup-${groupId}`"
      :data-id="groupId"
      @move="onMove"
      @add="onAdd"
      @remove="onRemove"
      @end="onEnd"
    >
      <div
        v-for="(elementId) in groupList"
        :key="elementId"
        class="group clearfix layer-container"
        :data-type="getElementById(elementId).type"
        :data-id="elementId"
      >
        <layer
          v-if="!getElementById(elementId).hide"
          :element="getElementById(elementId)"
          :group-id="groupId"
          :data-test="`graphics-layer-${elementId}`"
          @modal="(opts) => $emit('modal', opts)"
          @open-layer-menu="openLayerMenu"
        />

        <layers
          v-if="getElementGroupMap[elementId] && !getElementById(elementId).hide"
          class="sub-group"
          :class="{ 'sub-group--collapsed': getElementById(elementId).collapsed }"
          :group-id="elementId"
        />
      </div>
      <div
        v-if="groupId !== 'root' && groupList.length === 0"
        class="layer font-style-italic empty-layer"
        data-test="graphics-layers-emptyGroup"
      >
        Empty
      </div>
    </vue-draggable>
    <div
      v-if="layerMenu"
      ref="layerMenuEl"
      class="layer-menu-global"
      :style="{
        top: `${layerMenu.top}px`,
        left: `${layerMenu.left}px`,
      }"
      data-test="layers-menu-global"
      @click.stop
    >
      <button
        v-if="layerMenu.canUnGroup"
        type="button"
        class="layer-menu-global__item"
        data-test="layers-button-ungroup"
        @click="emitLayerAction('ungroup')"
      >
        Ungroup
      </button>

      <button
        v-if="layerMenu.canToggleLock"
        type="button"
        class="layer-menu-global__item"
        data-test="layers-button-togglelock"
        @click="emitLayerAction('toggle-lock')"
      >
        {{ layerMenu.isLocked ? 'Unlock' : 'Lock' }}
      </button>

      <div
        v-if="layerMenu.canToggleEditable"
        class="layer-menu-global__item layer-menu-global__item--checkbox"
        data-test="layers-item-editable"
      >
        <label class="checkbox">
          Editable (Producer)
          <input
            type="checkbox"
            :checked="layerMenu.isEditable"
            data-test="layers-checkbox-editable"
            @change.stop="toggleEditableFromMenu"
          >
          <div class="checkmark">
            <span class="checkmark-tick fa-solid fa-check" />
          </div>
        </label>
      </div>

      <button
        type="button"
        class="layer-menu-global__item"
        data-test="layers-button-rename"
        @click="emitLayerAction('rename')"
      >
        Rename
      </button>

      <button
        v-if="layerMenu.canDuplicate"
        type="button"
        class="layer-menu-global__item"
        data-test="layers-button-duplicate"
        @click="emitLayerAction('duplicate')"
      >
        Duplicate
      </button>

      <button
        v-if="layerMenu.canRefresh"
        type="button"
        class="layer-menu-global__item"
        data-test="layers-button-refresh"
        @click="emitLayerAction('refresh')"
      >
        Refresh
      </button>

      <button
        v-if="layerMenu.canDelete"
        type="button"
        class="layer-menu-global__item text-danger"
        data-test="layers-button-delete"
        @click="emitLayerAction('delete')"
      >
        Delete
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { VueDraggable } from 'vue-draggable-plus';
import EventBus from '../utils/event-bus';

import LayerComponent from './Layer.vue';
import toast from '../utils/toasts';

import {
  elementTypesAllowedInSheetData,
  elementTypesNotAllowedInGroup,
  marqueeChildElementTypes,
  illegalMarqueeParentTypes,
  elementTypesNotAllowedInShoppable,
} from '../utils/constants';

export default {
  name: 'Layers',
  components: {
    layer: LayerComponent,
    VueDraggable,
  },
  props: {
    groupId: {
      type: String,
      required: true,
    },
    list: {
      type: Array,
      default: null,
    },
  },
  emits: ['modal'],
  data() {
    return {
      layerMenu: null,
      instanceId: `lm_${Math.random().toString(36).slice(2)}`,
    };
  },
  computed: {
    groupList: {
      get() {
        return this.getElementGroupMap[this.groupId];
      },
      set(v) {
        this.setElementGroupMapById({
          value: {
            id: this.groupId,
            childIds: v,
          },
          options: {
            blacklist: true, // recording history after group positioning mutations are complete
          },
        });
      },
    },
    ...mapGetters('graphicsTemplate', [
      'getElementById',
      'getElementGroupMap',
      'getAllParents',
    ]),

    dragOptions() {
      return {
        animation: 200,
        group: { name: 'layers', pull: true, put: this.check },
        draggable: '.layer-container',
        ghostClass: 'ghost',
      };
    },
  },
  mounted() {
    document.addEventListener('click', this.onGlobalClick);
    window.addEventListener('keydown', this.onKeydown);
    window.addEventListener('scroll', this.onScroll, { capture: true });
    window.addEventListener('resize', this.onViewportChange);
    EventBus.on('layer-menu:open', this.onExternalMenuOpen);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onGlobalClick);
    window.removeEventListener('keydown', this.onKeydown);
    window.addEventListener('scroll', this.onScroll, { capture: true });
    window.removeEventListener('resize', this.onViewportChange);
    EventBus.off('layer-menu:open', this.onExternalMenuOpen);
  },

  methods: {
    ...mapActions('graphicsTemplate', ['setElementGroupMapById', 'updateRelativePositioning', 'takeHistorySnapshot', 'whenElementRemovedGroup', 'whenElementAddedViaDraggable']),

    onMove(e) {
      const element = this.getElementById(e.dragged?.dataset?.id);
      if (element?.data?.type === 'social-network-attribution') {
        toast.makeToast({
          heading: 'Information',
          icon: 'info',
          hideAfter: 4000,
          stack: false,
          text: 'Social Network Icon will remain on top in the canvas, regardless of its order in Layers.',
        });
      }
      return true;
    },

    // Validation to check if element can be nested inside another
    check(to, from, el) {
      const targetType = to.el?.dataset?.type;
      const type = el?.dataset?.type;
      const element = this.getElementById(el?.dataset?.id);
      const toElement = this.getElementById(to?.el?.dataset?.id);

      let fixedParentInGroup = false;
      if (element?.type === 'group') {
        fixedParentInGroup = this.getElementGroupMap[element.id]
          .some((c) => this.getElementById(c).fixedParent);
      }

      if (element?.fixedParent || fixedParentInGroup) {
        if (toElement) {
          const toElementsParents = this.getAllParents(toElement.id);
          const allowedParent = element.fixedParent || fixedParentInGroup;
          const allowedInside = toElement.id === allowedParent
            || toElementsParents.includes(allowedParent);
          if (allowedInside) {
            return true;
          }
        }
        return false;
      }

      // No slideshow-ception
      if (targetType === 'slideshow' && type === 'slideshow') {
        return false;
      }

      // No groups, group-style data elements, or slideshows inside groups
      if (targetType === 'group') {
        // TODO: If we want to enable groups inside of groups,
        // we'll need to revamp our DnD preview.
        // It's impossible/hard to manipulate nested elements at the moment
        return !elementTypesNotAllowedInGroup.includes(type);
      }

      // No groups, group-style data elements, or slideshows inside shoppable group
      if (targetType === 'shoppable') {
        // It's impossible/hard to manipulate nested elements at the moment
        if (type === 'qrcode') {
          return false;
        }
        return !elementTypesNotAllowedInShoppable.includes(type);
      }

      if (illegalMarqueeParentTypes.includes(targetType)) {
        if (type === 'marquee') {
          return false;
        }
      }

      // Only allow data-list elements inside of data providers (for now)
      // NOTE: This can be removed once data provider can inject into any element
      if (targetType === 'sheet-data') {
        return elementTypesAllowedInSheetData.includes(type);
      }

      // only allow marquee elements inside of marquee
      if (targetType === 'marquee') {
        return marqueeChildElementTypes.includes(type);
      }

      return true;
    },
    async onAdd(e) {
      await this.whenElementAddedViaDraggable({
        value: {
          groupToId: e.to.dataset.id,
          groupFromId: e.from.dataset.id,
          elementAddedId: e.item.dataset.id,
        },
        options: {
          blacklist: true,
        },
      });
      const excludedTypes = ['slideshow', 'data', 'sheet-data', 'social-data', 'sportradar'];
      const element = this.getElementById(e.item.dataset.id);
      if (e.to.dataset.id !== 'root' && !excludedTypes.includes(element.type)) {
        EventBus.emit('sync-all');
      }
    },
    onRemove(e) {
      this.whenElementRemovedGroup({
        value: {
          groupId: e.from.dataset.id,
        },
        options: {
          blacklist: true,
        },
      });
    },
    onEnd() {
      this.$nextTick(() => {
        this.takeHistorySnapshot();
      });
    },
    openLayerMenu(payload) {
      const { id, rect, ...meta } = payload;
      const padding = 6;

      EventBus.emit('layer-menu:open', { owner: this.instanceId });

      if (this.layerMenu && this.layerMenu.id === id) {
        this.closeLayerMenu();
        return;
      }

      this.layerMenu = {
        id,
        top: rect.bottom + padding,
        left: Math.max(rect.left, 8),
        visible: false,
        ...meta,
      };

      this.$nextTick(() => {
        const el = this.$refs.layerMenuEl;
        if (!el) return;

        const mw = el.offsetWidth || 180;
        const mh = el.offsetHeight || 160;

        // default: left-align to button, open below
        let { left } = rect;
        let top = rect.bottom + padding;

        // if overflowing right edge, fall back to right-align
        if (left + mw > window.innerWidth - 8) left = Math.max(8, rect.right - mw);
        if (left < 8) left = 8;

        // vertical flip if overflowing bottom
        if (top + mh > window.innerHeight - 8) top = rect.top - mh - padding;
        if (top < 8) top = 8;
        if (top + mh > window.innerHeight - 8) top = window.innerHeight - mh - 8;

        this.layerMenu.top = Math.round(top);
        this.layerMenu.left = Math.round(left);
        this.layerMenu.visible = true;
      });
    },

    closeLayerMenu() {
      this.layerMenu = null;
    },

    onGlobalClick() {
      if (this.layerMenu) {
        this.closeLayerMenu();
      }
    },

    onKeydown(e) {
      if (e.key === 'Escape' && this.layerMenu) {
        this.closeLayerMenu();
      }
    },

    onViewportChange() {
      if (this.layerMenu) {
        this.closeLayerMenu();
      }
    },

    onExternalMenuOpen(payload) {
      if (payload?.owner !== this.instanceId && this.layerMenu) {
        this.closeLayerMenu();
      }
    },

    onScroll(e) {
      if (this.layerMenu && !this.$refs.layerMenuEl?.contains(e.target)) {
        this.closeLayerMenu();
      }
    },

    emitLayerAction(action) {
      if (!this.layerMenu) return;

      EventBus.emit('layer-menu:action', {
        id: this.layerMenu.id,
        action,
      });

      this.closeLayerMenu();
    },
    toggleEditableFromMenu() {
      if (!this.layerMenu) return;

      EventBus.emit('layer-menu:action', {
        id: this.layerMenu.id,
        action: 'toggle-editable',
      });

      this.layerMenu = {
        ...this.layerMenu,
        isEditable: !this.layerMenu.isEditable,
      };
    },
  },
};
</script>
<style lang="scss" scoped>
.empty-layer {
  pointer-events: none;
}

.layer-menu-global {
  position: fixed;
  z-index: 9999;
  min-width: 150px;
  max-width: 220px;
  max-height: 60vh;
  padding: 6px 0;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  box-shadow:
    0 10px 25px rgba(15, 23, 42, 0.16),
    0 4px 8px rgba(15, 23, 42, 0.08);
  overflow: auto;
}

.layer-menu-global__item {
  width: 100%;
  padding: 6px 10px;
  text-align: left;
  font-size: 13px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;

  &:hover {
    background: #f1f5f9;
  }

  &.text-danger {
    color: #b91c1c;
  }
}
</style>
